import React, { useState, useEffect } from 'react';
import { grabService, ProductPlatformMapping } from '../services/grabService';
import { productsService } from '../services/productsService';
import { Product } from '../types';

// shadcn/ui components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/useToast';

interface ProductMappingFormProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  mapping?: ProductPlatformMapping | null;
}

export const ModernProductMappingForm: React.FC<ProductMappingFormProps> = ({
  open,
  onClose,
  onSave,
  mapping,
}) => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState({
    productId: '',
    platformItemId: '',
    platformCategoryId: '',
    syncEnabled: true,
  });

  useEffect(() => {
    if (open) {
      loadProducts();
      if (mapping) {
        setFormData({
          productId: mapping.productId,
          platformItemId: mapping.platformItemId,
          platformCategoryId: mapping.platformCategoryId || '',
          syncEnabled: mapping.syncEnabled,
        });
      } else {
        setFormData({
          productId: '',
          platformItemId: '',
          platformCategoryId: '',
          syncEnabled: true,
        });
      }
    }
  }, [open, mapping]);

  const loadProducts = async () => {
    try {
      const data = await productsService.getProducts();
      // Extract array from response (may include __contentRange, __total)
      const productsArray = Array.isArray(data) ? data : [];
      // Map to local Product type, extracting only needed fields
      setProducts(
        productsArray.map((p: any) => ({
          id: p.id,
          name: p.name || p.reference || '',
          reference: p.reference || '',
          price: p.price || 0,
          image: p.image || '',
          stock: p.stock || 0,
          thumbnail: p.thumbnail || '',
          categoryId: p.categoryId || '',
          description: p.description || '',
          height: p.height || 0,
          width: p.width || 0,
        })) as unknown as Product[]
      );
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const mappingData = {
        productId: formData.productId,
        platform: 'grab' as const,
        platformItemId: formData.platformItemId,
        platformCategoryId: formData.platformCategoryId || undefined,
        syncEnabled: formData.syncEnabled,
      };

      if (mapping) {
        await grabService.updateMapping(mapping.id, mappingData);
        success('Mapping updated successfully');
      } else {
        await grabService.createMapping(mappingData);
        success('Mapping created successfully');
      }

      onSave();
      onClose();
    } catch (err: any) {
      showError(err.message || 'Failed to save mapping');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mapping ? 'Edit Mapping' : 'Create Mapping'}
          </DialogTitle>
          <DialogDescription>
            {mapping
              ? 'Update product mapping to Grab platform'
              : 'Create a new product mapping to Grab platform'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="productId">Product *</Label>
              <Select
                value={formData.productId}
                onValueChange={(value) =>
                  setFormData({ ...formData, productId: value })
                }
                disabled={!!mapping}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem
                      key={String(product.id)}
                      value={String(product.id)}
                    >
                      {product.reference || String(product.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="platformItemId">Grab Item ID *</Label>
              <Input
                id="platformItemId"
                value={formData.platformItemId}
                onChange={(e) =>
                  setFormData({ ...formData, platformItemId: e.target.value })
                }
                placeholder="Enter Grab menu item ID"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platformCategoryId">Grab Category ID</Label>
              <Input
                id="platformCategoryId"
                value={formData.platformCategoryId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    platformCategoryId: e.target.value,
                  })
                }
                placeholder="Enter Grab category ID (optional)"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="syncEnabled"
                checked={formData.syncEnabled}
                onChange={(e) =>
                  setFormData({ ...formData, syncEnabled: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="syncEnabled" className="cursor-pointer">
                Enable sync
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : mapping ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
